/**
 * @file apps/web/src/components/UatCycleBrowser.tsx
 * @description Project -> UAT Cycle -> UAT Subcycle -> (Test Scripts | Defects) browser.
 * Deleting a cycle/subcycle does not delete its test scripts — subcycle_id
 * is nullable (ON DELETE SET NULL), so scripts become unassigned instead.
 * Unassigned scripts are reachable via the "Unassigned Scripts" link at the
 * Cycles level (level: 'unassigned' in DrillState), backed by the existing
 * GET /test-scripts?subcycleId=none query.
 * Defects have no subcycle_id of their own; they're scoped to a subcycle
 * indirectly via the test script they're raised against, so logging a
 * defect here requires picking one of the subcycle's scripts.
 */

import { useEffect, useState, useCallback } from 'react';
import {
  Plus, ChevronLeft, ChevronRight, Layers, FolderOpen, ClipboardList, Bug,
  Upload, X, AlertCircle, Trash2, AlertTriangle,
} from 'lucide-react';
import { uatCyclesApi, moduleTagsApi, testScriptsApi, uatApi, uatGapsApi, approvalsApi, projectsApi } from '../lib/api.js';
import { ScriptTable, type TestScript, type ScriptStep, type StepFormData, type TagRef } from './ScriptTable.js';
import { CSVImportModal, type CSVImportRow } from './CSVImportModal.js';
import { DefectList, type Defect } from './DefectCard.js';
import { GapList, type UatGap } from './GapCard.js';
import { SubmitForApprovalPanel } from './SubmitForApprovalPanel.js';
import { TemplatePickerModal } from './TemplatePickerModal.js';
import { ShieldCheck } from 'lucide-react';
import { useAuth } from '../lib/auth.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface UatCycle {
  id: string;
  name: string;
  description: string | null;
  subcycle_count: number;
  script_count: number;
}

interface UatSubcycle {
  id: string;
  cycle_id: string;
  name: string;
  description: string | null;
  script_count: number;
}

interface ModuleTag extends TagRef {
  script_count: number;
}

type H = Record<string, string>;

type DrillState =
  | { level: 'cycles' }
  | { level: 'subcycles'; cycle: UatCycle }
  | { level: 'scripts'; cycle: UatCycle; subcycle: UatSubcycle }
  | { level: 'unassigned' };

type SubTab = 'scripts' | 'defects' | 'gaps';

const TAG_SWATCHES = ['#0055FF', '#16A34A', '#D97706', '#DC2626', '#7C3AED', '#0891B2'];

const MODULES = [
  'finance', 'procurement', 'sales', 'inventory', 'manufacturing',
  'hr', 'payroll', 'projects', 'assets', 'other',
] as const;

function normalizeModule(raw: string): string {
  const m = raw.toLowerCase().trim();
  return (MODULES as readonly string[]).includes(m) ? m : 'other';
}

// ---------------------------------------------------------------------------
// Small inline "create" card — reused for cycles, subcycles, tags
// ---------------------------------------------------------------------------

function InlineCreateForm({
  placeholder,
  onSubmit,
  onCancel,
  withDescription = true,
}: {
  placeholder: string;
  onSubmit: (name: string, description: string) => Promise<void>;
  onCancel: () => void;
  withDescription?: boolean;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (!name.trim()) { setError('Name is required.'); return; }
    setSaving(true);
    setError('');
    try {
      await onSubmit(name.trim(), description.trim());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create');
      setSaving(false);
    }
  };

  return (
    <div className="card p-4 space-y-3">
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={placeholder}
        className="input"
        autoFocus
        onKeyDown={(e) => { if (e.key === 'Enter' && !withDescription) void submit(); if (e.key === 'Escape') onCancel(); }}
      />
      {withDescription && (
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Description (optional)"
          rows={2}
          className="input text-sm resize-none"
        />
      )}
      {error && (
        <div className="flex items-center gap-2 text-sm text-danger">
          <AlertCircle size={13} /> {error}
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="btn-ghost text-sm" disabled={saving}>Cancel</button>
        <button onClick={() => void submit()} disabled={saving || !name.trim()} className="btn-primary text-sm">
          {saving ? 'Creating…' : 'Create'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Log Defect form — requires picking a script from this subcycle, since
// defects are scoped to a subcycle only via their test_script_id.
// ---------------------------------------------------------------------------

function LogDefectForm({
  scripts,
  onSubmit,
  onCancel,
}: {
  scripts:  TestScript[];
  onSubmit: (d: { testScriptId: string; title: string; description: string; severity: string }) => Promise<void>;
  onCancel: () => void;
}) {
  const [testScriptId, setTestScriptId] = useState(scripts[0]?.id ?? '');
  const [title, setTitle]       = useState('');
  const [description, setDesc]  = useState('');
  const [severity, setSeverity] = useState('medium');
  const [saving, setSaving]     = useState(false);
  const [err, setErr]           = useState('');

  const handleSubmit = async () => {
    if (!testScriptId) { setErr('Select a test script.'); return; }
    if (!title.trim()) { setErr('Title is required.'); return; }
    setSaving(true);
    setErr('');
    try {
      await onSubmit({ testScriptId, title: title.trim(), description, severity });
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to log defect');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-ink">Log Defect</p>
        <button onClick={onCancel} className="btn-ghost text-xs text-muted">Cancel</button>
      </div>
      <div>
        <label className="label mb-1.5">Test Script *</label>
        <select value={testScriptId} onChange={(e) => setTestScriptId(e.target.value)} className="select text-sm">
          {scripts.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
        </select>
      </div>
      <div>
        <label className="label mb-1.5">Title *</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Brief description of the defect"
          className="input text-sm"
          autoFocus
        />
      </div>
      <div>
        <label className="label mb-1.5">Severity</label>
        <select
          value={severity}
          onChange={(e) => setSeverity(e.target.value)}
          className="select text-sm"
        >
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
      </div>
      <div>
        <label className="label mb-1.5">Description</label>
        <textarea
          value={description}
          onChange={(e) => setDesc(e.target.value)}
          placeholder="Steps to reproduce, expected vs actual…"
          className="input text-sm min-h-[80px] resize-none"
          rows={3}
        />
      </div>
      {err && (
        <div className="flex items-center gap-2 text-sm text-danger">
          <AlertCircle size={13} /> {err}
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="btn-ghost text-sm">Cancel</button>
        <button onClick={() => void handleSubmit()} disabled={saving} className="btn-primary text-sm">
          {saving ? 'Logging…' : 'Log Defect'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// UatCycleBrowser
// ---------------------------------------------------------------------------

export function UatCycleBrowser({ projectId, devHeaders }: { projectId: string; devHeaders: H }) {
  const { user } = useAuth();
  const [drill, setDrill] = useState<DrillState>({ level: 'cycles' });
  const [subTab, setSubTab] = useState<SubTab>('scripts');
  const [currentProjectRole, setCurrentProjectRole] = useState<string | undefined>(undefined);

  const [cycles, setCycles] = useState<UatCycle[]>([]);
  const [subcycles, setSubcycles] = useState<UatSubcycle[]>([]);
  const [scripts, setScripts] = useState<TestScript[]>([]);
  const [tags, setTags] = useState<ModuleTag[]>([]);
  const [defects, setDefects] = useState<Defect[]>([]);
  const [gaps, setGaps] = useState<UatGap[]>([]);
  const [unassignedScripts, setUnassignedScripts] = useState<TestScript[]>([]);
  const [unassignedCount, setUnassignedCount] = useState(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showNewCycle, setShowNewCycle] = useState(false);
  const [showNewSubcycle, setShowNewSubcycle] = useState(false);
  const [showNewTag, setShowNewTag] = useState(false);
  const [newTagColor, setNewTagColor] = useState(TAG_SWATCHES[0]!);
  const [csvOpen, setCsvOpen] = useState(false);
  const [showLogDefect, setShowLogDefect] = useState(false);
  const [packApproval, setPackApproval] = useState<{ id: string; status: string } | null>(null);
  const [showSubmitPackPanel, setShowSubmitPackPanel] = useState(false);
  const [templatePickerScriptId, setTemplatePickerScriptId] = useState<string | null>(null);

  // Active tag filter: undefined = All, 'untagged' = untagged-only, else a tag id
  const [tagFilter, setTagFilter] = useState<string | undefined>(undefined);

  // ---------------------------------------------------------------------
  // Loaders
  // ---------------------------------------------------------------------

  const loadCycles = useCallback(async () => {
    setLoading(true);
    try {
      const data = await uatCyclesApi.list(projectId, devHeaders) as UatCycle[];
      setCycles(data);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load UAT cycles');
    } finally {
      setLoading(false);
    }
  }, [projectId, devHeaders]);

  const loadSubcycles = useCallback(async (cycleId: string) => {
    setLoading(true);
    try {
      const data = await uatCyclesApi.listSubcycles(cycleId, devHeaders) as UatSubcycle[];
      setSubcycles(data);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load subcycles');
    } finally {
      setLoading(false);
    }
  }, [devHeaders]);

  const loadScripts = useCallback(async (subcycleId: string, filter: string | undefined) => {
    setLoading(true);
    try {
      const data = await testScriptsApi.list(projectId, devHeaders, {
        subcycleId,
        tagId: filter && filter !== 'untagged' ? filter : undefined,
        untagged: filter === 'untagged',
      }) as TestScript[];
      setScripts(data);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load test scripts');
    } finally {
      setLoading(false);
    }
  }, [projectId, devHeaders]);

  const loadTags = useCallback(async () => {
    try {
      const data = await moduleTagsApi.list(projectId, devHeaders) as ModuleTag[];
      setTags(data);
    } catch {
      // Tag bar is non-critical — leave silently empty on failure.
    }
  }, [projectId, devHeaders]);

  const loadDefects = useCallback(async (subcycleId: string) => {
    setLoading(true);
    try {
      const data = await uatApi.listDefects(projectId, devHeaders, { subcycleId }) as Defect[];
      setDefects(data);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load defects');
    } finally {
      setLoading(false);
    }
  }, [projectId, devHeaders]);

  const loadGaps = useCallback(async (subcycleId: string) => {
    setLoading(true);
    try {
      const data = await uatGapsApi.list(subcycleId, devHeaders) as UatGap[];
      setGaps(data);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load gaps');
    } finally {
      setLoading(false);
    }
  }, [devHeaders]);

  const loadPackApproval = useCallback(async (subcycleId: string) => {
    try {
      const data = await approvalsApi.forEntity('test_script_pack', subcycleId, devHeaders);
      setPackApproval(data as typeof packApproval);
    } catch {
      setPackApproval(null);
    }
  }, [devHeaders]);

  // Count only — drives the "N unassigned scripts" link at the Cycles level.
  // Failures are non-critical: leave the link hidden rather than surface an error.
  const loadUnassignedCount = useCallback(async () => {
    try {
      const data = await testScriptsApi.list(projectId, devHeaders, { subcycleId: 'none' }) as TestScript[];
      setUnassignedCount(data.length);
    } catch {
      // Non-critical — leave the link hidden on failure rather than surfacing an error.
    }
  }, [projectId, devHeaders]);

  const loadUnassigned = useCallback(async () => {
    setLoading(true);
    try {
      const data = await testScriptsApi.list(projectId, devHeaders, { subcycleId: 'none' }) as TestScript[];
      setUnassignedScripts(data);
      setUnassignedCount(data.length);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load unassigned scripts');
    } finally {
      setLoading(false);
    }
  }, [projectId, devHeaders]);

  useEffect(() => {
    if (drill.level === 'cycles') void loadCycles();
    if (drill.level === 'cycles') void loadUnassignedCount();
    if (drill.level === 'subcycles') void loadSubcycles(drill.cycle.id);
    if (drill.level === 'scripts') {
      void loadScripts(drill.subcycle.id, tagFilter);
      void loadTags();
      void loadPackApproval(drill.subcycle.id);
      if (subTab === 'defects') void loadDefects(drill.subcycle.id);
      if (subTab === 'gaps') void loadGaps(drill.subcycle.id);
    }
    if (drill.level === 'unassigned') void loadUnassigned();
  }, [drill, tagFilter, subTab, loadCycles, loadSubcycles, loadScripts, loadTags, loadPackApproval, loadDefects, loadGaps, loadUnassignedCount, loadUnassigned]);

  // Current user's project role — used to hide the lead-only Closed defect
  // transition from other users (server-enforced regardless; see
  // apps/api/src/routes/uat.ts's PATCH /defects/:id).
  useEffect(() => {
    let cancelled = false;
    projectsApi.members(projectId, devHeaders)
      .then((data) => {
        if (cancelled) return;
        const members = data as { id: string; role: string }[];
        setCurrentProjectRole(members.find((m) => m.id === user.userId)?.role);
      })
      .catch(() => { if (!cancelled) setCurrentProjectRole(undefined); });
    return () => { cancelled = true; };
  }, [projectId, devHeaders, user.userId]);

  // Reset to the Test Scripts sub-tab whenever a different subcycle is opened.
  useEffect(() => {
    if (drill.level === 'scripts') setSubTab('scripts');
  }, [drill.level === 'scripts' ? drill.subcycle.id : null]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------------
  // Cycle / subcycle mutations
  // ---------------------------------------------------------------------

  const handleCreateCycle = async (name: string, description: string) => {
    await uatCyclesApi.create({ projectId, name, description }, devHeaders);
    setShowNewCycle(false);
    await loadCycles();
  };

  const handleDeleteCycle = async (id: string) => {
    if (!confirm("Delete this UAT cycle? Its subcycles will be removed, and any test scripts inside them will become unassigned (visible under 'Unassigned Scripts').")) return;
    try {
      await uatCyclesApi.delete(id, devHeaders);
      await loadCycles();
      // Deleting a cycle can orphan the scripts in its subcycles — refresh
      // the "N unassigned scripts" count shown on this same view.
      await loadUnassignedCount();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete cycle');
    }
  };

  const handleCreateSubcycle = async (name: string, description: string) => {
    if (drill.level !== 'subcycles') return;
    await uatCyclesApi.createSubcycle(drill.cycle.id, { name, description }, devHeaders);
    setShowNewSubcycle(false);
    await loadSubcycles(drill.cycle.id);
  };

  const handleDeleteSubcycle = async (id: string) => {
    if (drill.level !== 'subcycles') return;
    if (!confirm("Delete this subcycle? Its test scripts will become unassigned rather than deleted (visible under 'Unassigned Scripts').")) return;
    try {
      await uatCyclesApi.deleteSubcycle(drill.cycle.id, id, devHeaders);
      await loadSubcycles(drill.cycle.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete subcycle');
    }
  };

  // ---------------------------------------------------------------------
  // Module tag mutations
  // ---------------------------------------------------------------------

  const handleCreateTag = async (name: string) => {
    try {
      await moduleTagsApi.create({ projectId, name, color: newTagColor }, devHeaders);
      setShowNewTag(false);
      await loadTags();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to create tag — this requires the project 'lead' role.");
    }
  };

  const handleDeleteTag = async (id: string) => {
    if (!confirm('Delete this module tag? It will be removed from every script that has it.')) return;
    try {
      await moduleTagsApi.delete(id, devHeaders);
      if (tagFilter === id) setTagFilter(undefined);
      await loadTags();
      if (drill.level === 'scripts') await loadScripts(drill.subcycle.id, tagFilter === id ? undefined : tagFilter);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to delete tag — this requires the project 'lead' role.");
    }
  };

  const handleToggleScriptTag = async (scriptId: string, tagId: string, hasTag: boolean) => {
    const tag = tags.find((t) => t.id === tagId);
    setScripts((prev) => prev.map((s) => s.id !== scriptId ? s : {
      ...s,
      tags: hasTag
        ? (s.tags ?? []).filter((t) => t.id !== tagId)
        : [...(s.tags ?? []), ...(tag ? [tag] : [])],
    }));
    try {
      if (hasTag) {
        await testScriptsApi.removeTag(scriptId, tagId, devHeaders);
      } else {
        await testScriptsApi.addTag(scriptId, tagId, devHeaders);
      }
      if (drill.level === 'scripts') await loadScripts(drill.subcycle.id, tagFilter);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update tag');
      if (drill.level === 'scripts') await loadScripts(drill.subcycle.id, tagFilter);
    }
  };

  // ---------------------------------------------------------------------
  // Test script mutations (mirrors the former TestScriptsPage, scoped to
  // this subcycle — every script created here always carries subcycleId)
  // ---------------------------------------------------------------------

  const handleAddScript = async () => {
    if (drill.level !== 'scripts') return;
    try {
      const created = await testScriptsApi.create({
        projectId,
        subcycleId: drill.subcycle.id,
        title: 'New Test Script',
        module: 'other',
        status: 'draft',
      }, devHeaders) as TestScript;
      setScripts((prev) => [...prev, created]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create script');
    }
  };

  const handleUpdateScript = async (id: string, field: string, value: string) => {
    setScripts((prev) => prev.map((s) => s.id === id ? { ...s, [field]: value } : s));
    try {
      await testScriptsApi.update(id, { [field]: value }, devHeaders);
    } catch (e) {
      // loadScripts clears `error` on success, so it must run BEFORE
      // setError below — otherwise the revert's own setError('') wipes
      // the message we're trying to surface.
      if (drill.level === 'scripts') await loadScripts(drill.subcycle.id, tagFilter);
      setError(e instanceof Error ? e.message : 'Update failed');
    }
  };

  const handleDeleteScript = async (id: string) => {
    if (!confirm('Delete this test script? It can be recovered within 30 days.')) return;
    setScripts((prev) => prev.filter((s) => s.id !== id));
    try {
      await testScriptsApi.delete(id, devHeaders);
    } catch (e) {
      // See handleUpdateScript — reload before setError so the message survives.
      if (drill.level === 'scripts') await loadScripts(drill.subcycle.id, tagFilter);
      setError(e instanceof Error ? e.message : 'Delete failed');
    }
  };

  const handleScriptApprovalCreated = () => {
    setTemplatePickerScriptId(null);
    if (drill.level === 'scripts') void loadScripts(drill.subcycle.id, tagFilter);
  };

  const handleSaveStep = async (scriptId: string, data: StepFormData) => {
    const script = scripts.find((s) => s.id === scriptId);
    const nextNum = (script?.steps?.length ?? 0) + 1;
    const newStep = await testScriptsApi.addStep(scriptId, {
      step_number:     nextNum,
      action:          data.action,
      expected_result: data.expected_result || null,
      test_data:       data.test_data || null,
    }, devHeaders) as ScriptStep;
    setScripts((prev) => prev.map((s) =>
      s.id === scriptId ? { ...s, steps: [...(s.steps ?? []), newStep] } : s,
    ));
  };

  const handleUpdateStep = async (scriptId: string, stepId: string, field: string, value: string) => {
    setScripts((prev) => prev.map((s) => {
      if (s.id !== scriptId) return s;
      return { ...s, steps: (s.steps ?? []).map((st) =>
        st.id === stepId ? { ...st, [field]: value } : st,
      )};
    }));
    try {
      await testScriptsApi.updateStep(scriptId, stepId, { [field]: value }, devHeaders);
    } catch {
      if (drill.level === 'scripts') await loadScripts(drill.subcycle.id, tagFilter);
    }
  };

  const handleDeleteStep = async (scriptId: string, stepId: string) => {
    setScripts((prev) => prev.map((s) => {
      if (s.id !== scriptId) return s;
      const filtered = (s.steps ?? []).filter((st) => st.id !== stepId);
      return { ...s, steps: filtered.map((st, i) => ({ ...st, step_number: i + 1 })) };
    }));
    try {
      await testScriptsApi.deleteStep(scriptId, stepId, devHeaders);
    } catch {
      if (drill.level === 'scripts') await loadScripts(drill.subcycle.id, tagFilter);
    }
  };

  const handleMoveStep = async (scriptId: string, stepId: string, direction: 'up' | 'down') => {
    const script = scripts.find((s) => s.id === scriptId);
    if (!script?.steps) return;
    const sorted = [...script.steps].sort((a, b) => a.step_number - b.step_number);
    const idx = sorted.findIndex((st) => st.id === stepId);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;

    const aNum = sorted[idx]!.step_number;
    const bNum = sorted[swapIdx]!.step_number;
    const reordered = sorted.map((st, i) => {
      if (i === idx)     return { ...st, step_number: bNum };
      if (i === swapIdx) return { ...st, step_number: aNum };
      return st;
    }).sort((a, b) => a.step_number - b.step_number);

    setScripts((prev) => prev.map((s) => s.id === scriptId ? { ...s, steps: reordered } : s));
    try {
      await testScriptsApi.updateStep(scriptId, sorted[idx]!.id!, { step_number: bNum }, devHeaders);
      await testScriptsApi.updateStep(scriptId, sorted[swapIdx]!.id!, { step_number: aNum }, devHeaders);
    } catch {
      if (drill.level === 'scripts') await loadScripts(drill.subcycle.id, tagFilter);
    }
  };

  // CSV import — every imported row is created inside the current subcycle.
  const handleImport = async (rows: CSVImportRow[]) => {
    if (drill.level !== 'scripts') return;
    const created = await Promise.all(
      rows.map((row) =>
        testScriptsApi.create({
          projectId,
          subcycleId: drill.subcycle.id,
          title:       row.title,
          module:      normalizeModule(row.module),
          status:      'draft',
          description: row.description ?? '',
          steps:       row.steps
            ? row.steps.split('|').filter(Boolean).map((desc, i) => ({
                step_number: i + 1,
                description: desc.trim(),
              }))
            : [],
        }, devHeaders)
      )
    ) as TestScript[];
    setScripts((prev) => [...prev, ...created]);
  };

  // ---------------------------------------------------------------------
  // Unassigned-script mutations — mirrors the Test script / step mutations
  // above exactly (same handler bodies, same testScriptsApi calls), just
  // scoped to unassignedScripts/setUnassignedScripts + loadUnassigned
  // instead of scripts/setScripts + loadScripts. Kept as separate handlers
  // rather than parameterizing the Level 3 ones because the Level 3
  // versions close over `drill.subcycle.id` / `tagFilter` for their reload
  // path, which doesn't apply here.
  // ---------------------------------------------------------------------

  const handleUpdateUnassignedScript = async (id: string, field: string, value: string) => {
    setUnassignedScripts((prev) => prev.map((s) => s.id === id ? { ...s, [field]: value } : s));
    try {
      await testScriptsApi.update(id, { [field]: value }, devHeaders);
    } catch {
      await loadUnassigned();
    }
  };

  const handleDeleteUnassignedScript = async (id: string) => {
    if (!confirm('Delete this test script? It can be recovered within 30 days.')) return;
    setUnassignedScripts((prev) => prev.filter((s) => s.id !== id));
    try {
      await testScriptsApi.delete(id, devHeaders);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
      await loadUnassigned();
    }
  };

  const handleSaveUnassignedStep = async (scriptId: string, data: StepFormData) => {
    const script = unassignedScripts.find((s) => s.id === scriptId);
    const nextNum = (script?.steps?.length ?? 0) + 1;
    const newStep = await testScriptsApi.addStep(scriptId, {
      step_number:     nextNum,
      action:          data.action,
      expected_result: data.expected_result || null,
      test_data:       data.test_data || null,
    }, devHeaders) as ScriptStep;
    setUnassignedScripts((prev) => prev.map((s) =>
      s.id === scriptId ? { ...s, steps: [...(s.steps ?? []), newStep] } : s,
    ));
  };

  const handleUpdateUnassignedStep = async (scriptId: string, stepId: string, field: string, value: string) => {
    setUnassignedScripts((prev) => prev.map((s) => {
      if (s.id !== scriptId) return s;
      return { ...s, steps: (s.steps ?? []).map((st) =>
        st.id === stepId ? { ...st, [field]: value } : st,
      )};
    }));
    try {
      await testScriptsApi.updateStep(scriptId, stepId, { [field]: value }, devHeaders);
    } catch {
      await loadUnassigned();
    }
  };

  const handleDeleteUnassignedStep = async (scriptId: string, stepId: string) => {
    setUnassignedScripts((prev) => prev.map((s) => {
      if (s.id !== scriptId) return s;
      const filtered = (s.steps ?? []).filter((st) => st.id !== stepId);
      return { ...s, steps: filtered.map((st, i) => ({ ...st, step_number: i + 1 })) };
    }));
    try {
      await testScriptsApi.deleteStep(scriptId, stepId, devHeaders);
    } catch {
      await loadUnassigned();
    }
  };

  const handleMoveUnassignedStep = async (scriptId: string, stepId: string, direction: 'up' | 'down') => {
    const script = unassignedScripts.find((s) => s.id === scriptId);
    if (!script?.steps) return;
    const sorted = [...script.steps].sort((a, b) => a.step_number - b.step_number);
    const idx = sorted.findIndex((st) => st.id === stepId);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;

    const aNum = sorted[idx]!.step_number;
    const bNum = sorted[swapIdx]!.step_number;
    const reordered = sorted.map((st, i) => {
      if (i === idx)     return { ...st, step_number: bNum };
      if (i === swapIdx) return { ...st, step_number: aNum };
      return st;
    }).sort((a, b) => a.step_number - b.step_number);

    setUnassignedScripts((prev) => prev.map((s) => s.id === scriptId ? { ...s, steps: reordered } : s));
    try {
      await testScriptsApi.updateStep(scriptId, sorted[idx]!.id!, { step_number: bNum }, devHeaders);
      await testScriptsApi.updateStep(scriptId, sorted[swapIdx]!.id!, { step_number: aNum }, devHeaders);
    } catch {
      await loadUnassigned();
    }
  };

  // ---------------------------------------------------------------------
  // Defect mutations
  // ---------------------------------------------------------------------

  // Shared by both defect-creation entry points: the Defects tab's own form
  // (script optional) and a step's "Log Defect" button (script required,
  // never the step — test_script_id is the only FK used). Throws on
  // failure so each caller's own inline error UI can display it.
  const createDefectFor = async (
    testScriptId: string | undefined,
    data: { title: string; description: string; severity: string },
  ): Promise<Defect> => {
    const created = await uatApi.createDefect({
      projectId,
      testScriptId,
      title:       data.title,
      description: data.description,
      severity:    data.severity,
    }, devHeaders) as Defect;
    setDefects((prev) => [created, ...prev]);
    return created;
  };

  const handleLogDefect = async (d: { testScriptId: string; title: string; description: string; severity: string }) => {
    await createDefectFor(d.testScriptId, d);
    setShowLogDefect(false);
  };

  // Triggered from a step marked fail/blocked (ScriptTable's onLogDefect).
  const handleLogDefectFromScript = async (scriptId: string, data: { title: string; description: string; severity: string }) => {
    await createDefectFor(scriptId, data);
  };

  const handleDefectStatusChange = async (id: string, status: string) => {
    setDefects((prev) => prev.map((d) => d.id === id ? { ...d, status } : d));
    try {
      await uatApi.updateDefect(id, { status }, devHeaders);
    } catch {
      if (drill.level === 'scripts') await loadDefects(drill.subcycle.id);
    }
  };

  // ---------------------------------------------------------------------
  // Breadcrumb
  // ---------------------------------------------------------------------

  function Breadcrumb() {
    if (drill.level === 'cycles') return null;
    return (
      <div className="flex items-center gap-1.5 text-xs font-mono text-muted mb-2">
        <button
          onClick={() => setDrill({ level: 'cycles' })}
          className="hover:text-blue transition-colors flex items-center gap-1"
        >
          <ChevronLeft size={12} /> Cycles
        </button>
        {drill.level === 'subcycles' && (
          <span className="text-ink">/ {drill.cycle.name}</span>
        )}
        {drill.level === 'scripts' && (
          <>
            <span>/</span>
            <button
              onClick={() => setDrill({ level: 'subcycles', cycle: drill.cycle })}
              className="hover:text-blue transition-colors"
            >
              {drill.cycle.name}
            </button>
            <span className="text-ink">/ {drill.subcycle.name}</span>
          </>
        )}
        {drill.level === 'unassigned' && (
          <span className="text-ink">/ Unassigned Scripts</span>
        )}
      </div>
    );
  }

  const openDefects = defects.filter((d) => d.status !== 'resolved' && d.status !== 'closed');
  const closedDefects = defects.filter((d) => d.status === 'resolved' || d.status === 'closed');

  const noGaps = gaps.filter((g) => g.native_support === 'none');
  const partialGaps = gaps.filter((g) => g.native_support === 'partial');

  const packLocked = packApproval?.status === 'approved';
  const packActive = packApproval ? ['pending', 'in_progress', 'approved'].includes(packApproval.status) : false;

  // ---------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------

  return (
    <div className="space-y-4">
      <Breadcrumb />

      {error && (
        <div className="flex items-center gap-2 p-3 bg-danger/8 rounded-lg text-danger text-sm">
          <AlertCircle size={14} className="shrink-0" /> {error}
        </div>
      )}

      {/* ------------------------------------------------------------- */}
      {/* Level 1: Cycle list                                           */}
      {/* ------------------------------------------------------------- */}
      {drill.level === 'cycles' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="label">UAT Cycles</p>
            <div className="flex items-center gap-2">
              {unassignedCount > 0 && (
                <button
                  onClick={() => setDrill({ level: 'unassigned' })}
                  className="btn-ghost text-xs text-muted hover:text-ink flex items-center gap-1.5"
                >
                  <AlertTriangle size={12} />
                  {unassignedCount} unassigned script{unassignedCount === 1 ? '' : 's'}
                </button>
              )}
              <button onClick={() => setShowNewCycle((v) => !v)} className="btn-secondary text-xs py-1 px-2">
                <Plus size={13} /> New Cycle
              </button>
            </div>
          </div>

          {showNewCycle && (
            <InlineCreateForm
              placeholder='e.g. "UAT 1"'
              onSubmit={handleCreateCycle}
              onCancel={() => setShowNewCycle(false)}
            />
          )}

          {loading ? (
            <div className="space-y-3 animate-pulse">
              {[1, 2].map((i) => <div key={i} className="card h-16 bg-gray/60" />)}
            </div>
          ) : cycles.length === 0 ? (
            <div className="empty-state py-12">
              <Layers size={24} className="text-muted/40 mb-2 mx-auto" />
              <p className="text-sm text-muted">No UAT cycles yet.</p>
              <button onClick={() => setShowNewCycle(true)} className="btn-secondary mt-4">
                <Plus size={14} /> New Cycle
              </button>
            </div>
          ) : (
            cycles.map((c) => (
              <div key={c.id} className="card p-4 flex items-center justify-between gap-3 group">
                <button
                  onClick={() => setDrill({ level: 'subcycles', cycle: c })}
                  className="flex-1 min-w-0 flex items-center gap-3 text-left"
                >
                  <Layers size={16} className="text-blue shrink-0" />
                  <div className="min-w-0">
                    <p className="font-medium text-sm text-ink">{c.name}</p>
                    <p className="text-[11px] font-mono text-muted mt-0.5">
                      {c.subcycle_count} subcycle{c.subcycle_count !== 1 ? 's' : ''} · {c.script_count} script{c.script_count !== 1 ? 's' : ''}
                    </p>
                  </div>
                </button>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => handleDeleteCycle(c.id)}
                    className="btn-icon w-7 h-7 text-muted opacity-0 group-hover:opacity-100 transition-opacity hover:text-danger"
                    title="Delete cycle"
                  >
                    <Trash2 size={13} />
                  </button>
                  <ChevronRight size={16} className="text-muted/50" />
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* ------------------------------------------------------------- */}
      {/* Level 2: Subcycle list (within a cycle)                       */}
      {/* ------------------------------------------------------------- */}
      {drill.level === 'subcycles' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="label">Subcycles</p>
            <button onClick={() => setShowNewSubcycle((v) => !v)} className="btn-secondary text-xs py-1 px-2">
              <Plus size={13} /> New Subcycle
            </button>
          </div>

          {showNewSubcycle && (
            <InlineCreateForm
              placeholder={`e.g. "${drill.cycle.name}.1"`}
              onSubmit={handleCreateSubcycle}
              onCancel={() => setShowNewSubcycle(false)}
            />
          )}

          {loading ? (
            <div className="space-y-3 animate-pulse">
              {[1, 2].map((i) => <div key={i} className="card h-16 bg-gray/60" />)}
            </div>
          ) : subcycles.length === 0 ? (
            <div className="empty-state py-12">
              <FolderOpen size={24} className="text-muted/40 mb-2 mx-auto" />
              <p className="text-sm text-muted">No subcycles yet.</p>
              <button onClick={() => setShowNewSubcycle(true)} className="btn-secondary mt-4">
                <Plus size={14} /> New Subcycle
              </button>
            </div>
          ) : (
            subcycles.map((sc) => (
              <div key={sc.id} className="card p-4 flex items-center justify-between gap-3 group">
                <button
                  onClick={() => setDrill({ level: 'scripts', cycle: drill.cycle, subcycle: sc })}
                  className="flex-1 min-w-0 flex items-center gap-3 text-left"
                >
                  <FolderOpen size={16} className="text-blue shrink-0" />
                  <div className="min-w-0">
                    <p className="font-medium text-sm text-ink">{sc.name}</p>
                    <p className="text-[11px] font-mono text-muted mt-0.5">
                      {sc.script_count} script{sc.script_count !== 1 ? 's' : ''}
                    </p>
                  </div>
                </button>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => handleDeleteSubcycle(sc.id)}
                    className="btn-icon w-7 h-7 text-muted opacity-0 group-hover:opacity-100 transition-opacity hover:text-danger"
                    title="Delete subcycle"
                  >
                    <Trash2 size={13} />
                  </button>
                  <ChevronRight size={16} className="text-muted/50" />
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* ------------------------------------------------------------- */}
      {/* Level 3: Test Scripts / Defects tabs within a subcycle         */}
      {/* ------------------------------------------------------------- */}
      {drill.level === 'scripts' && (
        <div className="space-y-4">
          {/* Sub-tab bar */}
          <div className="flex gap-1 border-b border-[rgba(8,17,31,0.08)]">
            {([
              { key: 'scripts' as const, label: 'Test Scripts', icon: <ClipboardList size={14} />,  count: scripts.length },
              { key: 'defects' as const, label: 'Defects',      icon: <Bug size={14} />,            count: openDefects.length },
              { key: 'gaps' as const,    label: 'Gaps',         icon: <AlertTriangle size={14} />,  count: gaps.length },
            ]).map((t) => (
              <button
                key={t.key}
                onClick={() => setSubTab(t.key)}
                className={[
                  'flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors',
                  subTab === t.key ? 'border-blue text-blue' : 'border-transparent text-muted hover:text-ink',
                ].join(' ')}
              >
                {t.icon}
                {t.label}
                {t.count > 0 && (
                  <span className={[
                    'text-[10px] font-mono px-1.5 py-0.5 rounded-full',
                    subTab === t.key ? 'bg-blue/10 text-blue' : 'bg-gray text-muted',
                  ].join(' ')}>
                    {t.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {drill.level === 'scripts' && !showSubmitPackPanel && !packLocked && (
            <div className="flex justify-end">
              <button onClick={() => setShowSubmitPackPanel(true)} className="btn-secondary text-xs py-1 px-2">
                <ShieldCheck size={13} /> Submit for Approval
              </button>
            </div>
          )}

          {drill.level === 'scripts' && showSubmitPackPanel && (
            <SubmitForApprovalPanel
              projectId={projectId}
              entityType="test_script_pack"
              entityId={drill.subcycle.id}
              title={`Test Script Pack Approval — ${drill.subcycle.name}`}
              onCreated={(created) => {
                setPackApproval(created as typeof packApproval);
                setShowSubmitPackPanel(false);
              }}
              onCancel={() => setShowSubmitPackPanel(false)}
            />
          )}

          {drill.level === 'scripts' && packApproval && !packLocked && (
            <p className="text-xs font-mono text-muted">
              Pack approval is {packApproval.status}.
            </p>
          )}

          {templatePickerScriptId && (
            <TemplatePickerModal
              projectId={projectId}
              entityType="test_script"
              entityId={templatePickerScriptId}
              title="Submit Test Script for Approval"
              onCreated={handleScriptApprovalCreated}
              onCancel={() => setTemplatePickerScriptId(null)}
            />
          )}

          {/* --------------------------------------------------------- */}
          {/* Test Scripts sub-tab                                       */}
          {/* --------------------------------------------------------- */}
          {subTab === 'scripts' && (
            <div className="space-y-3">
              {/* Tag filter bar */}
              <div className="flex flex-wrap items-center gap-1.5">
                <button
                  onClick={() => setTagFilter(undefined)}
                  className={[
                    'text-[11px] font-mono px-2.5 py-1 rounded-full border transition-colors',
                    tagFilter === undefined ? 'bg-blue text-white border-blue' : 'border-[rgba(8,17,31,0.12)] text-muted hover:text-ink',
                  ].join(' ')}
                >
                  All
                </button>
                {tags.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setTagFilter(t.id)}
                    className={[
                      'flex items-center gap-1 text-[11px] font-mono px-2.5 py-1 rounded-full border transition-colors',
                      tagFilter === t.id ? 'bg-blue text-white border-blue' : 'border-[rgba(8,17,31,0.12)] text-muted hover:text-ink',
                    ].join(' ')}
                  >
                    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: tagFilter === t.id ? '#fff' : (t.color ?? '#0055FF') }} />
                    {t.name}
                    <span className="opacity-60">{t.script_count}</span>
                  </button>
                ))}
                <button
                  onClick={() => setTagFilter('untagged')}
                  className={[
                    'text-[11px] font-mono px-2.5 py-1 rounded-full border transition-colors',
                    tagFilter === 'untagged' ? 'bg-blue text-white border-blue' : 'border-[rgba(8,17,31,0.12)] text-muted hover:text-ink',
                  ].join(' ')}
                >
                  Untagged
                </button>
                <button
                  onClick={() => setShowNewTag((v) => !v)}
                  className="text-[11px] font-mono px-2 py-1 rounded-full border border-dashed border-[rgba(8,17,31,0.2)] text-muted hover:text-blue hover:border-blue transition-colors flex items-center gap-1"
                >
                  <Plus size={11} /> Tag
                </button>
              </div>

              {showNewTag && (
                <div className="card p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    {TAG_SWATCHES.map((color) => (
                      <button
                        key={color}
                        onClick={() => setNewTagColor(color)}
                        className="w-5 h-5 rounded-full shrink-0"
                        style={{ background: color, boxShadow: newTagColor === color ? '0 0 0 2px #fff, 0 0 0 4px ' + color : 'none' }}
                      />
                    ))}
                  </div>
                  <InlineCreateForm
                    placeholder='e.g. "Procurement"'
                    withDescription={false}
                    onSubmit={(name) => handleCreateTag(name)}
                    onCancel={() => setShowNewTag(false)}
                  />
                  <p className="text-[10px] font-mono text-muted">Creating a tag requires the project lead role.</p>
                </div>
              )}

              {tags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {tags.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => handleDeleteTag(t.id)}
                      className="text-[10px] font-mono text-muted/60 hover:text-danger transition-colors flex items-center gap-0.5"
                      title={`Delete tag "${t.name}"`}
                    >
                      <X size={9} /> {t.name}
                    </button>
                  ))}
                </div>
              )}

              <div className="flex items-center justify-between pt-1">
                <p className="label">
                  {scripts.length} script{scripts.length !== 1 ? 's' : ''}
                </p>
                <div className="flex items-center gap-2">
                  <button onClick={() => setCsvOpen(true)} className="btn-secondary text-xs py-1 px-2">
                    <Upload size={13} /> Import CSV
                  </button>
                  <button onClick={() => void handleAddScript()} className="btn-secondary text-xs py-1 px-2">
                    <Plus size={13} /> New Script
                  </button>
                </div>
              </div>

              <ScriptTable
                scripts={scripts}
                loading={loading}
                locked={packLocked}
                packApprovalActive={packActive}
                onSubmitForApproval={(scriptId) => setTemplatePickerScriptId(scriptId)}
                onUpdate={handleUpdateScript}
                onDelete={handleDeleteScript}
                onAddScript={handleAddScript}
                onSaveStep={handleSaveStep}
                onUpdateStep={handleUpdateStep}
                onDeleteStep={handleDeleteStep}
                onMoveStep={handleMoveStep}
                allTags={tags}
                onToggleTag={handleToggleScriptTag}
                onLogDefect={handleLogDefectFromScript}
              />
            </div>
          )}

          {/* --------------------------------------------------------- */}
          {/* Defects sub-tab                                            */}
          {/* --------------------------------------------------------- */}
          {subTab === 'defects' && (
            <div className="space-y-4">
              <div className="flex items-center justify-end">
                <button
                  onClick={() => setShowLogDefect((v) => !v)}
                  disabled={scripts.length === 0}
                  className="btn-primary text-sm"
                  title={scripts.length === 0 ? 'Add a test script to this subcycle first' : undefined}
                >
                  <Plus size={14} /> Log Defect
                </button>
              </div>

              {scripts.length === 0 && (
                <p className="text-xs font-mono text-muted">
                  Add a test script to this subcycle before logging a defect against it.
                </p>
              )}

              {showLogDefect && (
                <LogDefectForm
                  scripts={scripts}
                  onSubmit={handleLogDefect}
                  onCancel={() => setShowLogDefect(false)}
                />
              )}

              {openDefects.length > 0 && (
                <div>
                  <p className="label mb-2">Open ({openDefects.length})</p>
                  <DefectList
                    defects={openDefects}
                    loading={loading}
                    onStatusChange={handleDefectStatusChange}
                    currentProjectRole={currentProjectRole}
                  />
                </div>
              )}
              {closedDefects.length > 0 && (
                <div>
                  <p className="label mb-2">Resolved / Closed ({closedDefects.length})</p>
                  <DefectList
                    defects={closedDefects}
                    loading={false}
                    onStatusChange={handleDefectStatusChange}
                    currentProjectRole={currentProjectRole}
                  />
                </div>
              )}
              {!loading && defects.length === 0 && (
                <div className="empty-state py-12">
                  <Bug size={24} className="text-muted/40 mb-2 mx-auto" />
                  <p className="text-sm text-muted">No defects logged for this subcycle yet.</p>
                </div>
              )}
            </div>
          )}

          {/* --------------------------------------------------------- */}
          {/* Gaps sub-tab                                               */}
          {/* --------------------------------------------------------- */}
          {subTab === 'gaps' && (
            <div className="space-y-4">
              {gaps.length > 0 && (
                <p className="text-sm font-mono text-muted">
                  {gaps.length} gap{gaps.length !== 1 ? 's' : ''} detected — {noGaps.length} require customization, {partialGaps.length} {partialGaps.length !== 1 ? 'are' : 'is'} partial native support
                </p>
              )}

              <GapList
                gaps={gaps}
                loading={loading}
                emptyMessage="No gaps detected for this subcycle yet."
              />
            </div>
          )}
        </div>
      )}

      {/* ------------------------------------------------------------- */}
      {/* Unassigned Scripts: test scripts whose subcycle was deleted    */}
      {/* (subcycle_id set to null). No defects/gaps sub-tabs here —     */}
      {/* those are scoped to a subcycle, which these scripts no longer  */}
      {/* have.                                                          */}
      {/* ------------------------------------------------------------- */}
      {drill.level === 'unassigned' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="label">
              {unassignedScripts.length} unassigned script{unassignedScripts.length !== 1 ? 's' : ''}
            </p>
          </div>

          <ScriptTable
            scripts={unassignedScripts}
            loading={loading}
            onUpdate={handleUpdateUnassignedScript}
            onDelete={handleDeleteUnassignedScript}
            onSaveStep={handleSaveUnassignedStep}
            onUpdateStep={handleUpdateUnassignedStep}
            onDeleteStep={handleDeleteUnassignedStep}
            onMoveStep={handleMoveUnassignedStep}
          />
        </div>
      )}

      {/* CSV import modal */}
      {drill.level === 'scripts' && (
        <CSVImportModal
          open={csvOpen}
          onClose={() => setCsvOpen(false)}
          onImport={handleImport}
          projectId={projectId}
        />
      )}
    </div>
  );
}
